/**
 * @module aircraft.service
 */
import { DialVerseApi } from '../app/api'
import AppConfig from '../config'

const AircraftService = DialVerseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Basic CRUD operations
    getAircraft: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/aircraft'),
        method: 'GET',
      }),
    }),
    getAircraftById: builder.mutation({
      query: (aircraftId) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}`),
        method: 'GET',
      }),
    }),
    getAircraftByRegistration: builder.mutation({
      query: (registrationNumber) => ({
        url: AppConfig.getUrl(`/aircraft/registration/${registrationNumber}`),
        method: 'GET',
      }),
    }),
    createAircraft: builder.mutation({
      query: (aircraft) => ({
        url: AppConfig.getUrl('/aircraft'),
        method: 'POST',
        body: aircraft,
      }),
    }),
    updateAircraft: builder.mutation({
      query: ({ aircraftId, ...updates }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}`),
        method: 'PUT',
        body: updates,
      }),
    }),
    deleteAircraft: builder.mutation({
      query: (aircraftId) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}`),
        method: 'DELETE',
      }),
    }),

    // Status and profile updates
    updateAircraftStatus: builder.mutation({
      query: ({ aircraftId, status }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/status`),
        method: 'PATCH',
        body: { status },
      }),
    }),
    updateSpecifications: builder.mutation({
      query: ({ aircraftId, specifications }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/specifications`),
        method: 'PATCH',
        body: { specifications },
      }),
    }),
    updateConfiguration: builder.mutation({
      query: ({ aircraftId, configuration }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/configuration`),
        method: 'PATCH',
        body: { configuration },
      }),
    }),
    updateMaintenance: builder.mutation({
      query: ({ aircraftId, maintenance }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/maintenance`),
        method: 'PATCH',
        body: { maintenance },
      }),
    }),
    updateOperationalInfo: builder.mutation({
      query: ({ aircraftId, operationalInfo }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/operational-info`),
        method: 'PATCH',
        body: { operationalInfo },
      }),
    }),
    updateAirworthiness: builder.mutation({
      query: ({ aircraftId, airworthiness }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/airworthiness`),
        method: 'PATCH',
        body: { airworthiness },
      }),
    }),
    updateInsurance: builder.mutation({
      query: ({ aircraftId, insurance }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/insurance`),
        method: 'PATCH',
        body: { insurance },
      }),
    }),

    // Modification operations
    addModification: builder.mutation({
      query: ({ aircraftId, modification }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/modifications`),
        method: 'POST',
        body: { modification },
      }),
    }),
    removeModification: builder.mutation({
      query: ({ aircraftId, modification }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/modifications`),
        method: 'DELETE',
        body: { modification },
      }),
    }),

    // Maintenance operations
    updateMaintenanceStatus: builder.mutation({
      query: ({ aircraftId, maintenanceStatus }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/maintenance/status`),
        method: 'PATCH',
        body: { maintenanceStatus },
      }),
    }),
    addMaintenanceRecord: builder.mutation({
      query: ({ aircraftId, maintenanceRecord }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/maintenance/records`),
        method: 'POST',
        body: { maintenanceRecord },
      }),
    }),
    updateAirworthinessDirective: builder.mutation({
      query: ({ aircraftId, airworthinessDirective }) => ({
        url: AppConfig.getUrl(`/aircraft/${aircraftId}/maintenance/directives`),
        method: 'PATCH',
        body: { airworthinessDirective },
      }),
    }),

    // Query operations
    getActiveAircraft: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/aircraft/status/active'),
        method: 'GET',
      }),
    }),
    getAircraftNeedingMaintenance: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/aircraft/status/needs-maintenance'),
        method: 'GET',
      }),
    }),
    getAircraftByAirline: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/aircraft/airline/${airlineId}`),
        method: 'GET',
      }),
    }),
    getAircraftByType: builder.mutation({
      query: (type) => ({
        url: AppConfig.getUrl(`/aircraft/type/${type}`),
        method: 'GET',
      }),
    }),
    getAircraftByLocation: builder.mutation({
      query: (location) => ({
        url: AppConfig.getUrl(`/aircraft/location/${location}`),
        method: 'GET',
      }),
    }),

    // Compliance and monitoring
    getAircraftWithExpiringAirworthiness: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/aircraft/compliance/expiring-airworthiness'),
        method: 'GET',
      }),
    }),
    getAircraftWithExpiringInsurance: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/aircraft/compliance/expiring-insurance'),
        method: 'GET',
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetAircraftMutation,
  useGetAircraftByIdMutation,
  useGetAircraftByRegistrationMutation,
  useCreateAircraftMutation,
  useUpdateAircraftMutation,
  useDeleteAircraftMutation,
  useUpdateAircraftStatusMutation,
  useUpdateSpecificationsMutation,
  useUpdateConfigurationMutation,
  useUpdateMaintenanceMutation,
  useUpdateOperationalInfoMutation,
  useUpdateAirworthinessMutation,
  useUpdateInsuranceMutation,
  useAddModificationMutation,
  useRemoveModificationMutation,
  useUpdateMaintenanceStatusMutation,
  useAddMaintenanceRecordMutation,
  useUpdateAirworthinessDirectiveMutation,
  useGetActiveAircraftMutation,
  useGetAircraftNeedingMaintenanceMutation,
  useGetAircraftByAirlineMutation,
  useGetAircraftByTypeMutation,
  useGetAircraftByLocationMutation,
  useGetAircraftWithExpiringAirworthinessMutation,
  useGetAircraftWithExpiringInsuranceMutation,
} = AircraftService
